/**
 * Step 4 — who the batch is going to. Three ways in, all landing on the same
 * create call, which validates and answers with the per-row report.
 *
 * The CSV is **not** parsed in the browser: the file goes to the server, whose
 * generated header row is the contract. That is why there is no column-mapping
 * UI here — the columns are generated rather than guessed.
 */

import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { handleErrorMessage, openErrorNotification } from "helpers/methods";
import { bulkShellStyles, recipientsStyles } from "../BulkSend.styles";
import tokens from "../BulkSend.tokens";
import {
  BulkCreateResponse,
  BulkRowCheckSummary,
  BulkSignerSlot,
  BULK_MAX_ROWS,
} from "../BulkSendTypesAndValidation";
import {
  downloadBlob,
  mergeServerIssues,
  summaryFromReport,
} from "../BulkSendHelper";
import { fetchBulkCsvTemplate } from "../BulkSendService";
import SavedListPicker from "./SavedListPicker";
import RecipientsCheckPanel from "./RecipientsCheckPanel";
import TypedRowsGrid, {
  cellInputId,
  TypedCell,
  TypedRow,
} from "./TypedRowsGrid";
import {
  DownloadIcon,
  GridIcon,
  InfoIcon,
  PeopleIcon,
  UploadCloudIcon,
} from "./BulkSendIcons";

const p = tokens.palette;
const t = tokens.recipients;
const s = recipientsStyles;

type RecipientRoute = "upload" | "typed" | "saved";

interface Props {
  templateId: string;
  templateName: string;
  /** Roles the list fills — their names are the CSV's column prefixes. */
  varyingSlots: BulkSignerSlot[];
  fixedSignerIndexes: number[];
  report: BulkCreateResponse | null;
  /** Changes when rows arrive from a file or a saved list; 0 before any have. */
  importedAt: number;
  isSubmitting: boolean;
  typedRows: TypedRow[];
  /** Derived once by the wizard and shared by the table and the panel. */
  typedSummary: BulkRowCheckSummary;
  onTypedChange: (
    rowIndex: number,
    signerIndex: number,
    cell: TypedCell,
  ) => void;
  onTypedAdd: () => void;
  onTypedRemove: (rowIndex: number) => void;
  onTypedSkip: (rowIndex: number) => void;
  onUseSavedList: (listId: string, listName: string) => void;
  onUpload: (file: File) => void;
}

const ROUTES: { key: RecipientRoute; label: string }[] = [
  { key: "upload", label: "Upload a file" },
  { key: "typed", label: "Type rows" },
  { key: "saved", label: "From a saved list" },
];

const BulkRecipientsStep: React.FC<Props> = ({
  templateId,
  templateName,
  varyingSlots,
  fixedSignerIndexes,
  report,
  importedAt,
  isSubmitting,
  typedRows,
  typedSummary,
  onTypedChange,
  onTypedAdd,
  onTypedRemove,
  onTypedSkip,
  onUseSavedList,
  onUpload,
}) => {
  const [route, setRoute] = useState<RecipientRoute>("upload");

  /*
    Rows that came from a file or a saved list are still rows, so they are
    shown where rows are edited. The sender lands on the grid holding what
    their file contained, rather than on a report about it.
  */
  useEffect(() => {
    if (importedAt > 0) setRoute("typed");
  }, [importedAt]);
  const [isDownloading, setIsDownloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const roleNames = varyingSlots.map((slot) => slot.label).join(", ");

  const handleDownloadTemplate = async () => {
    try {
      setIsDownloading(true);
      const blob = await fetchBulkCsvTemplate(templateId, fixedSignerIndexes);
      const safeName = (templateName || "bulk-send")
        .replace(/[^a-z0-9]+/gi, "-")
        .toLowerCase();
      downloadBlob(blob, `${safeName}-recipients.csv`);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      openErrorNotification("Choose a .csv file — that's the format we read.");
      return;
    }
    /*
      A header-only file 400s the whole upload. Parsing stays on the server;
      this only checks some line after the header has a value in it. Excel
      pads emptied rows out to `,,,`, so those count as blank.
    */
    const dataLines = (await file.text())
      .split(/\r?\n/)
      .slice(1)
      .filter((line) => line.replace(/[,"\s]/g, "") !== "");
    if (dataLines.length === 0) {
      openErrorNotification(
        "This file has no recipients — add one row per recipient under the header.",
      );
      return;
    }
    onUpload(file);
  };

  return (
    <Box sx={bulkShellStyles.stepPage("list")}>
      <Box sx={s.header}>
        <Box>
          <Typography component="h1" sx={bulkShellStyles.pageTitle}>
            Recipients
          </Typography>
          <Typography component="p" sx={{ ...bulkShellStyles.pageSub, mb: 0 }}>
            Each recipient becomes one request for{" "}
            <Box component="b" sx={{ color: p.body }}>
              {roleNames || "the varying role"}
            </Box>
            . Up to {BULK_MAX_ROWS} per batch.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={handleDownloadTemplate}
          disabled={isDownloading}
          startIcon={<DownloadIcon size={t.downloadIconSize} />}
          sx={{
            height: t.downloadHeight,
            borderRadius: t.downloadRadius,
            fontSize: t.downloadFontSize,
            whiteSpace: "nowrap",
          }}
        >
          {isDownloading ? "Preparing…" : "Download CSV template"}
        </Button>
      </Box>

      <Box sx={s.routes} role="tablist" aria-label="How to add recipients">
        {ROUTES.map((option) => (
          <Box
            key={option.key}
            component="button"
            type="button"
            role="tab"
            aria-selected={route === option.key}
            onClick={() => setRoute(option.key)}
            sx={s.route(route === option.key)}
          >
            {option.key === "upload" && (
              <UploadCloudIcon size={t.routeIconSize} />
            )}
            {option.key === "typed" && <GridIcon size={t.routeIconSize} />}
            {option.key === "saved" && <PeopleIcon size={t.routeIconSize} />}
            {option.label}
          </Box>
        ))}
      </Box>

      {route === "upload" && (
        <>
          <Box
            component="button"
            type="button"
            sx={s.dropzone}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(event: React.DragEvent) => event.preventDefault()}
            onDrop={(event: React.DragEvent) => {
              event.preventDefault();
              handleFile(event.dataTransfer.files?.[0]);
            }}
            disabled={isSubmitting}
          >
            <UploadCloudIcon
              size={t.dropzoneIconSize}
              color={p.ink}
              strokeWidth={1.6}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "7px" }}>
              <Box component="span" sx={s.dropzoneTitle}>
                {isSubmitting
                  ? "Checking your file…"
                  : "Drop your CSV here, or click to browse"}
              </Box>
              <Box component="span" sx={s.dropzoneHint}>
                Use the template above — its columns are built from this
                document's roles and fields.
              </Box>
            </Box>
          </Box>
          <Box
            component="input"
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            sx={{ display: "none" }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              handleFile(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          {report ? (
            <RecipientsCheckPanel
              summary={summaryFromReport(report)}
              onFix={() => undefined}
            />
          ) : (
            <Box sx={s.note}>
              <InfoIcon size={t.noteIconSize} color={p.body} />
              <Box component="span">
                We check every row before anything is sent, and tell you which
                ones can't be delivered.
              </Box>
            </Box>
          )}
        </>
      )}

      {route === "typed" && (
        <>
          <TypedRowsGrid
            slots={varyingSlots}
            rows={typedRows}
            summary={typedSummary}
            onChange={onTypedChange}
            onAdd={onTypedAdd}
            onRemove={onTypedRemove}
          />
          {/*
            The check opens under the table, never in place of it: the row a
            sender has to fix is still in front of them, and every number here
            comes from the same pass the table's own marks read.
          */}
          {(report || typedSummary.checked > 0) && (
            <RecipientsCheckPanel
              summary={mergeServerIssues(typedSummary, report, varyingSlots)}
              onFix={(check) => {
                if (check.signerIndex === undefined || !check.field) return;
                const input = document.getElementById(
                  cellInputId(check.index, check.signerIndex, check.field),
                );
                input?.focus();
                input?.scrollIntoView({ block: "center", behavior: "smooth" });
              }}
              onSkip={onTypedSkip}
            />
          )}
        </>
      )}

      {route === "saved" && report && (
        <RecipientsCheckPanel
          summary={summaryFromReport(report)}
          onFix={() => undefined}
        />
      )}

      {route === "saved" && !report && (
        <SavedListPicker
          isSubmitting={isSubmitting}
          onUse={onUseSavedList}
          onUploadInstead={() => setRoute("upload")}
        />
      )}
    </Box>
  );
};

export default BulkRecipientsStep;
